'use client';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Section from '@repo/ui/src/components/Section';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { ToutContentProps } from "@repo/ui/src/types/toutContent";
import { MediaType } from '@repo/ui/src/types/image';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@style/variables';
import { useIsSM, useIsMD, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';

gsap.registerPlugin(ScrollTrigger);

interface AllGrillsHeroProps {
  toutContent: ToutContentProps;
  largeGrillImage: MediaType;
  smallGrillImage: MediaType;
}

const AllGrillsHero = ({ toutContent, largeGrillImage, smallGrillImage }: AllGrillsHeroProps) => {
  const mdAndUp = useIsMD();
  const smAndUp = useIsSM();
  const reduceMotion = useReducedMotion();
  const [imagesLoaded, setImagesLoaded] = useState({
    grill: false,
    smoke1: false,
    smoke2: false
  });

  const imageSize = smAndUp ? imageFullWidthSmAndUp : imageFullWidthXs;

  const smokeImage = {
    endpoint: 'traeger',
    name: 'transparent-smoke',
  };

  const smokeImgUrl = buildImageSrc(smokeImage, imageSize, 0, 0);

  const grillImgUrl = buildImageSrc(
    mdAndUp ? largeGrillImage : smallGrillImage,
    imageSize,
    0,
    0,
  );

  const fadeDelay = smAndUp ? 5 : 7;

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const hero = element.querySelector('.series-hero') as HTMLElement;
    const grillContent = element.querySelector('.grill-hero-content') as HTMLElement;
    const smokeWrapper = element.querySelector('.smoke-wrapper') as HTMLElement;
    const smokeImage1 = element.querySelector('.smoke-image-1 img') as HTMLImageElement;
    const smokeImage2 = element.querySelector('.smoke-image-2 img') as HTMLImageElement;

    const heroGrillTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: hero,
        start: 'top center',
        end: 'bottom 40%',
        scrub: 0.5,
      },
    });

    heroGrillTimeline
      .addLabel('start')
      .fromTo(
        grillContent,
        { translateY: reduceMotion ? 0 : 100, opacity: 0 },
        { translateY: 0, opacity: 1, duration: 1 },
      )
      .delay(fadeDelay)
      .addLabel('smoke')
      .fromTo(
        smokeWrapper,
        { yPercent: reduceMotion ? 0 : 100, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 3 },
        'smoke',
      )
      .fromTo(
        smokeImage1,
        { xPercent: reduceMotion ? 0 : -12 },
        { xPercent: 0, duration: 3 },
        'smoke',
      )
      .fromTo(
        smokeImage2,
        { xPercent: reduceMotion ? 0 : 6 },
        { xPercent: 0, duration: 3 },
        'smoke',
      );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [fadeDelay, reduceMotion]);

  return (
    <section ref={ref} className='grill-hero-content'>
      <Section dark texture className="series-hero sm:tw-min-h-[480px] !-tw-z-10">
        <ToutContent
          dark
          divider
          flexible
          {...toutContent}
          headingStyleVariant="h1"
          className="tw-overflow-hidden tw-text-center tw-px-6 sm:tw-pb-[30vh] sm:tw-pt-[14rem] md:tw-pt-[128px] md:tw-pb-[40vh] tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-flex tw-flex-col tw-justify-center tw-h-[70vh] md:tw-h-[100vh]"
        />

        <div className="tw-relative tw-overflow-hidden tw-pt-[55vh]">
          <div className="tw-m-auto tw-w-full tw-px-6 after:tw-absolute after:tw-bottom-[-1px] after:tw-h-[37vw] md:after:tw-h-[24vw] after:tw-z-101 after:tw-bg-fixed after:tw-w-full after:tw-left-0 after:tw-bg-speckle after:tw-bg-[#f6f4f3]">
            <div
              className="tw-z-[102] tw-relative tw-transition-opacity tw-duration-500 tw-ease-in-out"
              style={{
                opacity: imagesLoaded.grill ? 1 : 0,
              }}
            >
              <Image
                src={grillImgUrl}
                alt={largeGrillImage?.name || "Traeger Grills"}
                width={imageSize}
                height={imageSize}
                className="tw-relative tw-w-full tw-block"
                id="series-grill-image"
                priority={true}
                onLoadingComplete={() => setImagesLoaded(prev => ({ ...prev, grill: true }))}
              />
            </div>
          </div>
          <div className="smoke-wrapper tw-absolute tw-mb-[20vw] sm:tw-mb-[25vh] tw-left-0 tw-bottom-0 tw-w-full tw-opacity-0 tw-translate-y-full tw-z-[100]">
            <div className="smoke-image-1 tw-w-[110%] tw-absolute tw-bottom-0 tw-z-[101] tw-transition-opacity tw-duration-500 tw-ease-in-out"
              style={{
                opacity: imagesLoaded.smoke1 ? 1 : 0,
              }}
            >
              <Image
                src={smokeImgUrl}
                width={imageSize}
                height={imageSize}
                role="presentation"
                alt=""
                priority={true}
                className="tw-absolute tw-w-full tw-left-0 tw-right-auto tw-bottom-0"
                onLoadingComplete={() => setImagesLoaded(prev => ({ ...prev, smoke1: true }))}
              />
            </div>
            <div className="smoke-image-2 tw-z-[101] tw-relative tw-transition-opacity tw-duration-500 tw-ease-in-out"
              style={{
                opacity: imagesLoaded.smoke2 ? 1 : 0,
              }}
            >
              <Image
                src={smokeImgUrl}
                width={imageSize}
                height={imageSize}
                role="presentation"
                alt=""
                className="tw-absolute tw-w-full tw-left-auto tw-right-0 tw-bottom-0 -tw-scale-x-100"
                priority={true}
                onLoadingComplete={() => setImagesLoaded(prev => ({ ...prev, smoke2: true }))}
              />
            </div>
          </div>
        </div>
      </Section>
    </section>
  );
};

export default AllGrillsHero;
